import { useState } from 'react'
import { AlertTriangle, FileText } from 'lucide-react'
import { useParams } from 'react-router'
import {
  CurrencyValue, ErrorState, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { SettlementComponentTable } from '../components/SettlementComponentTable'
import { useSettlement, useSettlementMutations } from '../api'
import { LEAVING_REASON_LABELS, SETTLEMENT_STATUS_LABELS } from '../types'
import type { LeavingReason, SettlementStatus } from '../types'

const STATUS_TONE: Record<SettlementStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  CALCULATED: 'warning',
  APPROVED: 'info',
  PAID: 'success',
  CANCELLED: 'neutral',
}

/**
 * One settlement, with every component carrying the words that produced it.
 *
 * A component that could not be computed shows its reason instead of an amount. That is a
 * different statement from an entitlement of zero, and the screen must not render them alike:
 * one means "you are owed nothing", the other means "nobody has configured this yet".
 */
export function SettlementDetailPage() {
  const { id } = useParams<{ id: string }>()
  const settlement = useSettlement(id)
  useBreadcrumb(settlement.data?.reference ?? 'Settlement')
  const { calculate, approve, cancel, statement } = useSettlementMutations()
  const [error, setError] = useState<string | null>(null)

  if (settlement.isPending) return <Skeleton className="h-64" />
  if (settlement.isError) {
    return <ErrorState error={settlement.error} onRetry={() => void settlement.refetch()} />
  }
  if (!settlement.data) return null

  const current = settlement.data
  const status = current.status as SettlementStatus
  const earnings = current.lines.filter((line) => line.direction === 'EARNING')
  const deductions = current.lines.filter((line) => line.direction === 'DEDUCTION')

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${current.reference} — ${current.employeeName ?? 'Unknown'}`}
        subtitle={`${LEAVING_REASON_LABELS[current.leavingReason as LeavingReason]
          ?? current.leavingReason} · last worked ${current.lastWorkingDate}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {(status === 'DRAFT' || status === 'CALCULATED') && (
              <Button
                type="button"
                onClick={() => act(
                  () => calculate.mutateAsync(current.id),
                  'That settlement could not be calculated')}
              >
                {status === 'DRAFT' ? 'Calculate' : 'Recalculate'}
              </Button>
            )}
            {status === 'CALCULATED' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => act(
                    () => cancel.mutateAsync({ id: current.id }),
                    'That settlement could not be cancelled')}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => act(
                    () => approve.mutateAsync({ id: current.id }),
                    'That settlement could not be approved')}
                >
                  Approve
                </Button>
              </>
            )}
            {status !== 'DRAFT' && (
              <Button
                type="button"
                variant="outline"
                onClick={() => act(
                  () => statement.mutateAsync(current.id),
                  'The statement could not be produced')}
              >
                <FileText className="size-4" aria-hidden="true" />
                Produce the statement
              </Button>
            )}
            {current.statementFileId && (
              <Button asChild variant="outline">
                <a href={`/api/v1/files/${current.statementFileId}/content`}>Open the PDF</a>
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card className="p-4">
        <dl className="grid gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-slate-600">Status</dt>
            <dd className="mt-1">
              <StatusBadge
                status={current.status}
                tone={STATUS_TONE[status] ?? 'neutral'}
                label={SETTLEMENT_STATUS_LABELS[status] ?? current.status}
              />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Service</dt>
            <dd className="mt-1">
              {current.serviceYears}y {current.serviceMonths}m {current.serviceDays}d
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Owed</dt>
            <dd className="mt-1 font-medium">
              <CurrencyValue value={current.grossAmount} currency={current.currency} />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Net payable</dt>
            <dd className="mt-1 font-medium">
              <CurrencyValue value={current.netAmount} currency={current.currency} />
            </dd>
          </div>
        </dl>

        {current.basicWageBasis && (
          <p className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <strong>The wage this rests on: </strong>{current.basicWageBasis}
          </p>
        )}

        {current.unavailableComponentCount > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {current.unavailableComponentCount === 1
                ? 'One component could not be computed'
                : `${current.unavailableComponentCount} components could not be computed`}
              {' '}and contributes nothing to the total. Each is listed below with its reason.
              This is not the same as an entitlement of zero — if one of them should have paid
              something, the configuration behind it is missing rather than the entitlement.
            </span>
          </p>
        )}
      </Card>

      <SettlementComponentTable title="What is owed" lines={earnings}
        total={current.grossAmount} currency={current.currency} />
      {deductions.length > 0 && (
        <SettlementComponentTable title="What is recovered" lines={deductions}
          total={current.deductionAmount} currency={current.currency} />
      )}

      {current.configSnapshot && (
        <Card className="p-4">
          <h2 className="text-sm font-semibold">Terms applied</h2>
          <p className="mt-2 font-mono text-xs text-slate-600">{current.configSnapshot}</p>
          <p className="mt-2 text-xs text-slate-600">
            The terms as they stood when this was calculated, not as they stand today.
          </p>
        </Card>
      )}
    </div>
  )
}
